import React, { useMemo } from "react";
import { connect } from "react-redux";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { defineMessage, useIntl } from "react-intl";

import { ClosedWonDeals } from "@app/scene/reports/store/OverviewData";
import { getOverviewData } from "@app/scene/reports/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { doesWeekStartFromMonday } from "@app/util/dates";
import { formatDate } from "@app/util/formatters";

import NoChart from "../../charts/NoChart";

const dealsWonOverTimeChartTitle = defineMessage({
  id: "reports.overview.chart.dealsWonOverTime",
  defaultMessage: "Aggregate Deals Won Over Time",
  description: "Aggregate Deals Won Over Time chart title on the Overview page in Reports",
});

interface Props {
  dealsByDate: ClosedWonDeals;
}

const DealsWonOverTimeChart: React.FC<Props> = ({ dealsByDate }) => {
  const intl = useIntl();

  const dealsWonOverTime = useMemo(() => {
    let y = 0;
    const response = [];
    for (const [key, value] of dealsByDate.entries()) {
      y += value.count;
      response.push({
        name: formatDate(key, "PPP"),
        x: key,
        y,
      });
    }
    return response;
  }, [dealsByDate]);

  const chartDataDealsWonOverTime: Highcharts.Options = useMemo(
    () => ({
      chart: {
        height: 350,
        plotBackgroundColor: undefined,
        plotBorderWidth: undefined,
        plotShadow: false,
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          colorByPoint: true,
          data: dealsWonOverTime,
          name: "# Deals",
          type: "line",
        },
      ],
      time: {
        timezoneOffset: new Date().getTimezoneOffset(),
      },
      title: {
        text: intl.formatMessage(dealsWonOverTimeChartTitle),
      },
      tooltip: {
        pointFormat: "{series.name}: <b>{point.y}</b>",
      },
      xAxis: {
        labels: {
          formatter: function () {
            return formatDate(this.value, "P");
          },
        },
        startOfWeek: doesWeekStartFromMonday() ? 1 : 0,
        type: "datetime",
      },
      yAxis: {
        title: {
          text: null,
        },
      },
    }),
    [intl, dealsWonOverTime]
  );

  return dealsWonOverTime.length > 0 ? (
    <HighchartsReact highcharts={Highcharts} options={chartDataDealsWonOverTime} />
  ) : (
    <NoChart chartTitle={intl.formatMessage(dealsWonOverTimeChartTitle)} />
  );
};

const mapStateToProps = (state: RootState) => ({
  dealsByDate: getOverviewData(state).closedWonDeals,
});

export default connect(mapStateToProps)(DealsWonOverTimeChart);
